import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Input, Tooltip, Typography } from 'antd';
import { Copy, Link, Plus, Search } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Pager } from '@/components/Pager';
import { usePagination } from '@/hooks';
import { AdminAssessmentPackagesService, BaseAPIClient } from '@/services/api';
import type { AssessmentPackage } from '@/services/api';
import { buildPackageBuyUrl, containerVariants, itemVariants } from '@/utils';
import { APP_CURRENCY } from '@/utils/constants/currency';
import { ROUTES, buildRoute } from '@/routes';
import styles from './AdminPackages.module.scss';

const { Title, Text } = Typography;

const PER_PAGE = 9;

export const PackageList: React.FC = () => {
  const { getToken } = useAuth();
  const { notification } = App.useApp();
  const navigate = useNavigate();

  const copyBuyLink = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(buildPackageBuyUrl(id));
      notification.success({ message: 'Purchase link copied' });
    } catch {
      notification.error({ message: 'Could not copy link' });
    }
  };

  const [packages, setPackages] = useState<AssessmentPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const svc = new AdminAssessmentPackagesService(token);
      setPackages(await svc.list());
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getToken, notification]);

  useEffect(() => {
    void fetchPackages();
  }, [fetchPackages]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return packages.filter((p) => {
      if (!term) return true;
      return p.name.toLowerCase().includes(term) || (p.description ?? '').toLowerCase().includes(term);
    });
  }, [packages, search]);

  const { page, pageCount, pageItems, from, to, total, setPage } = usePagination(filtered, PER_PAGE);

  const openEditor = (id: string) => navigate(buildRoute(ROUTES.ADMIN_PACKAGE_EDIT, { id }));

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div className={styles.pageHead} variants={itemVariants}>
        <div>
          <Title level={2} className={styles.pageTitle}>
            Assessment Packages
          </Title>
          <Text className={styles.pageSub}>
            Bundle a question set with its credit pricing so candidates can purchase it. One
            question set per package.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<Plus style={{ width: '1em', height: '1em' }} />}
          className={styles.btnPrimary}
          onClick={() => navigate(ROUTES.ADMIN_PACKAGE_NEW)}
        >
          Add package
        </Button>
      </motion.div>

      <motion.div className={styles.toolbar} variants={itemVariants}>
        <div className={styles.search}>
          <Search className={styles.searchIcon} style={{ width: '1em', height: '1em' }} />
          <Input
            placeholder="Search packages…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className={styles.searchInput}
          />
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
      {loading ? (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Loading…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>
            {packages.length ? 'No packages match' : 'No assessment packages yet'}
          </div>
          <div className={styles.emptySub}>
            {packages.length
              ? 'Adjust the search.'
              : 'Use "Add package" to bundle a question set for candidates to purchase.'}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.cardGrid}>
            {pageItems.map((pkg) => (
              <div
                key={pkg.id}
                className={styles.card}
                onClick={() => openEditor(pkg.id)}
                role="button"
                tabIndex={0}
              >
                <div className={styles.cardTitleRow}>
                  <div className={styles.cardTitle}>{pkg.name}</div>
                </div>
                <div className={styles.cardDesc}>{pkg.description || '—'}</div>
                <div className={styles.cardMetaLabel}>QUESTION SET</div>
                <div className={styles.cardMetaValue}>
                  {pkg.questionSet.name}
                  {pkg.questionSet.htsQuestionnaireId !== null && (
                    <span className={styles.htsBadge}>HTS</span>
                  )}
                </div>
                <div className={styles.creditsCallout}>
                  <span className={styles.creditsDollarSign}>$</span>
                  <span className={styles.creditsNumber}>{(pkg.unlockPriceCents / 100).toFixed(2)}</span>
                  <span className={styles.creditsUnit}>{APP_CURRENCY}</span>
                </div>

                <Tooltip title="Copy purchase link">
                  <div
                    className={styles.buyLinkFooter}
                    role="button"
                    tabIndex={0}
                    aria-label="Copy purchase link"
                    onClick={(e) => copyBuyLink(e, pkg.id)}
                  >
                    <Link className={styles.buyLinkIcon} style={{ width: '1em', height: '1em' }} />
                    <span className={styles.buyLinkText}>{buildPackageBuyUrl(pkg.id)}</span>
                    <Copy className={styles.buyLinkCopy} style={{ width: '1em', height: '1em' }} />
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>
          {filtered.length > PER_PAGE && (
            <Pager page={page} pageCount={pageCount} from={from} to={to} total={total} onChange={setPage} />
          )}
        </>
      )}
      </motion.div>
    </motion.div>
  );
};

export default PackageList;
